import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Row, Col, Card, Button, Form, Spinner, ListGroup } from 'react-bootstrap';
import { LuCircleCheck, LuMapPin, LuClipboard, LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { checkout } from '../../redux/reducers/portalOrderSlice';
import { clearCart } from '../../redux/reducers/portalCartSlice';
import { formatCurrency } from '../../utils/currency';

const PortalCheckout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businessId } = useParams();
    const { cart } = useSelector((state) => state.portalCart);
    const { customer, businessData } = useSelector((state) => state.portalAuth);
    const { loading, currentOrder } = useSelector((state) => state.portalOrder);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');
    
    const stripeConfig = businessData?.stripeConfig || {};
    const hasStripeEnabled = !!(stripeConfig.stripeConnectId && 
                               stripeConfig.onboardingComplete && 
                               stripeConfig.chargesEnabled);

    const taxByBusiness = businessData?.taxByBusiness || {};
    const taxLabel = taxByBusiness.label || 'Tax';
    const taxType = taxByBusiness.type || 'bypass';
    const taxPercentage = taxByBusiness.percentage || 0;
    
    const subtotal = cart?.totals?.subtotal || 0;
    const taxAmount = taxType === 'percentage' ? (subtotal * taxPercentage / 100) : 0;
    const grandTotal = subtotal + taxAmount;

    const [note, setNote] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(hasStripeEnabled ? 'STRIPE' : 'PAY_ON_DELIVERY');
    const [orderPlaced, setOrderPlaced] = useState(false);

    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({
        recipientName: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phone: '',
    });

    const paymentPolicy = businessData?.paymentPolicy || {};
    const showPayOnDelivery = paymentPolicy.payOnDelivery ?? true;

    useEffect(() => {
        if (businessData) {
            const stripeCfg = businessData.stripeConfig || {};
            const isStripeActive = !!(stripeCfg.stripeConnectId && 
                                     stripeCfg.onboardingComplete && 
                                     stripeCfg.chargesEnabled);
            setPaymentMethod(isStripeActive ? 'STRIPE' : 'PAY_ON_DELIVERY');
        }
    }, [businessData]);

    useEffect(() => {
        if (!cart?.items?.length && !orderPlaced) {
            navigate(`/customer-side/${businessId}/cart`);
        }
    }, [cart, navigate, orderPlaced, businessId]);

    useEffect(() => {
        const activeCustomer = cart?.customerId || customer;
        if (activeCustomer) {
            setAddressForm({
                recipientName: activeCustomer.name || '',
                street: activeCustomer.address?.street || '',
                city: activeCustomer.address?.city || '',
                state: activeCustomer.address?.state || '',
                zipCode: activeCustomer.address?.zipCode || '',
                country: activeCustomer.address?.country || 'Canada',
                phone: activeCustomer.phone?.number || activeCustomer.phone || '',
            });
        }
    }, [customer, cart]);

    const handlePlaceOrder = async () => {
        const result = await dispatch(checkout({ 
            customerNote: note,
            paymentMethod: paymentMethod,
            deliveryAddress: addressForm
        }));
        if (!result.error) {
            const { invoice } = result.payload;
            if (paymentMethod === 'STRIPE' && invoice?.paymentUrl) {
                window.location.href = invoice.paymentUrl;
            } else {
                setOrderPlaced(true);
                dispatch(clearCart());
            }
        }
    };

    if (orderPlaced && currentOrder) {
        return (
            <div className="text-center py-5 mt-5">
                <LuCircleCheck size={80} className="text-success mb-4" />
                <h1 className="fw-bold mb-3">Order Placed Successfully!</h1>
                <p className="text-muted fs-5 mb-5 mx-auto" style={{ maxWidth: '600px' }}>
                    Your order <strong>{currentOrder.orderNumber}</strong> has been received. 
                    An invoice has been sent to your email with a payment link.
                </p>
                <div className="d-flex gap-3 justify-content-center">
                    <Button as={Link} to={`/customer-side/${businessId}/orders/${currentOrder._id}`} className="rounded-3 px-4 py-2 text-white border-0" style={{ background: "#28B0C4" }}>
                        View Order Details
                    </Button>
                    <Button as={Link} to={`/customer-side/${businessId}/dashboard`} variant="outline-secondary" className="rounded-3 px-4 py-2">
                        Back to Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="fw-bold text-dark mb-4">Checkout</h2>

            <Row className="g-4">
                <Col lg={7}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <LuMapPin size={24} style={{ color: "#28B0C4" }} />
                                    <h5 className="fw-bold mb-0">Delivery Address</h5>
                                </div>
                                {!isEditingAddress && (
                                    <Button 
                                        variant="link" 
                                        className="text-decoration-none p-0 fw-semibold" 
                                        style={{ color: "#28B0C4", fontSize: "14px" }}
                                        onClick={() => setIsEditingAddress(true)}
                                    >
                                        Ship to New Address
                                    </Button>
                                )}
                            </div>
                            
                            {!isEditingAddress ? (
                                <div className="bg-light rounded-4 p-4 mb-3">
                                    <div className="fw-bold text-dark mb-1">{addressForm.recipientName || 'No recipient set'}</div>
                                    <div className="text-muted">
                                        {addressForm.street || 'No street address set'}<br />
                                        {addressForm.city || 'No city'}, {addressForm.state || 'no state'} {addressForm.zipCode || ''}<br />
                                        {addressForm.country || ''}
                                    </div>
                                    <div className="text-muted mt-2">
                                        Phone: {formatPhoneNumber(addressForm.phone) || 'Not provided'}
                                    </div>
                                </div>
                            ) : (
                                <Form className="mb-3">
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">Recipient Name</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.recipientName}
                                                    onChange={(e) => setAddressForm({ ...addressForm, recipientName: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="Recipient Name"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">Phone Number</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.phone}
                                                    onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="Phone Number"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">Street Address</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.street}
                                                    onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="Street Address"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">City</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.city}
                                                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="City"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">State / Province</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.state}
                                                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="State"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">Zip / Postal Code</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.zipCode}
                                                    onChange={(e) => setAddressForm({ ...addressForm, zipCode: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="Zip / Postal Code"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted fw-bold mb-1">Country</Form.Label>
                                                <Form.Control 
                                                    type="text" 
                                                    value={addressForm.country}
                                                    onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                                                    className="rounded-3 border-light bg-light shadow-none p-2.5 small"
                                                    placeholder="Country"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                        <Button variant="light" className="rounded-3 px-3 py-2 small fw-medium" onClick={() => setIsEditingAddress(false)}>
                                            Cancel
                                        </Button>
                                        <Button className="rounded-3 px-3 py-2 small fw-medium text-white border-0" style={{ background: "#28B0C4" }} onClick={() => setIsEditingAddress(false)}>
                                            Save Address
                                        </Button>
                                    </div>
                                </Form>
                            )}
                            
                            <p className="small text-muted mb-0">
                                * Editing this address only affects this particular order. Your profile delivery address remains unchanged.
                            </p>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuClipboard size={24} style={{ color: "#28B0C4" }} />
                                <h5 className="fw-bold mb-0">Order Notes (Optional)</h5>
                            </div>
                            
                            <Form.Control 
                                as="textarea" 
                                rows={4} 
                                placeholder="Add any instructions or notes for the business..."
                                className="rounded-4 border-light p-3 bg-light shadow-none"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 mt-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuCircleCheck size={24} style={{ color: "#28B0C4" }} />
                                <h5 className="fw-bold mb-0">Payment Method</h5>
                            </div>
                            
                            <div className="d-flex flex-column gap-3">
                                {hasStripeEnabled && (
                                    <div 
                                        className={`p-3 rounded-4 border-2 cursor-pointer transition-all ${paymentMethod === 'STRIPE' ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-light'}`}
                                        onClick={() => setPaymentMethod('STRIPE')}
                                        style={{ borderStyle: 'solid', cursor: 'pointer' }}
                                    >
                                        <Form.Check 
                                            type="radio"
                                            label={
                                                <div className="ms-2">
                                                    <div className="fw-bold text-dark">Pay Online (Card)</div>
                                                    <div className="text-muted small">Secure payment via Stripe</div>
                                                </div>
                                            }
                                            name="paymentMethod"
                                            checked={paymentMethod === 'STRIPE'}
                                            onChange={() => setPaymentMethod('STRIPE')}
                                        />
                                    </div>
                                )}

                                {showPayOnDelivery && (
                                    <div 
                                        className={`p-3 rounded-4 border-2 cursor-pointer transition-all ${paymentMethod === 'PAY_ON_DELIVERY' ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-light'}`}
                                        onClick={() => setPaymentMethod('PAY_ON_DELIVERY')}
                                        style={{ borderStyle: 'solid', cursor: 'pointer' }}
                                    >
                                        <Form.Check 
                                            type="radio"
                                            label={
                                                <div className="ms-2">
                                                    <div className="fw-bold text-dark">Cash On Delivery</div>
                                                    <div className="text-muted small">Pay when you receive your items</div>
                                                </div>
                                            }
                                            name="paymentMethod"
                                            id="payment-pod"
                                            checked={paymentMethod === 'PAY_ON_DELIVERY'}
                                            onChange={() => setPaymentMethod('PAY_ON_DELIVERY')}
                                        />
                                    </div>
                                )}

                                {!hasStripeEnabled && !showPayOnDelivery && (
                                    <div className="p-3 text-center rounded-4 border border-danger text-danger bg-danger bg-opacity-10 small">
                                        No payment methods are currently configured for this business. Please contact support.
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={5}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <h5 className="fw-bold mb-4">Order Review</h5>
                            
                            <ListGroup variant="flush" className="mb-4">
                                {cart?.items?.map((item) => (
                                    <ListGroup.Item key={item.productId} className="px-0 py-3 bg-transparent border-light">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="pe-3">
                                                <div className="fw-bold text-dark small">{item.productName}</div>
                                                <div className="text-muted extra-small">Qty: {item.quantity} × {formatCurrency(item.unitPrice, currency)}</div>
                                            </div>
                                            <div className="fw-bold text-dark small">
                                                {formatCurrency(item.lineTotal, currency)}
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            
                            <div className="d-flex justify-content-between mb-2 text-muted small">
                                <span>Subtotal</span>
                                <span>{formatCurrency(subtotal, currency)}</span>
                            </div>
                            {taxType === 'percentage' && (
                                <div className="d-flex justify-content-between mb-2 text-muted small">
                                    <span>{taxLabel} ({taxPercentage}%)</span>
                                    <span>{formatCurrency(taxAmount, currency)}</span>
                                </div>
                            )}
                            <div className="d-flex justify-content-between mb-4 text-muted small">
                                <span>Shipping Estimate</span>
                                <span className="text-success fw-semibold">Will be calculated by Business</span>
                            </div>
                            
                            <hr className="mb-4" />
                            
                            <div className="d-flex justify-content-between mb-4">
                                <h5 className="fw-bold">Total</h5>
                                <h5 className="fw-bold" style={{ color: "#28B0C4" }}>{formatCurrency(grandTotal, currency)}</h5>
                            </div>
                            
                            <Button 
                                className="w-100 rounded-3 py-3 fw-bold text-white border-0 d-flex align-items-center justify-content-center gap-2 shadow-lg"
                                style={{ background: "#28B0C4" }}
                                onClick={handlePlaceOrder}
                                disabled={loading || (!hasStripeEnabled && !showPayOnDelivery)}
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : <>Place Order <LuArrowRight size={18} /></>}
                            </Button>
                            
                            <div className="mt-4 text-center">
                                <p className="text-muted extra-small">
                                    By placing an order, you agree to the business's terms of service and refund policies.
                                </p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PortalCheckout;
